"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Hammer, Droplets, PaintRoller, House, Building2, WandSparkles, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  title: "Garage Door Repair",
  description: "Fast, reliable fixes for broken springs, snapped cables, and malfunctioning openers.",
  icon: Hammer,
  subServices: ["Spring Replacement", "Cable Repair", "Opener Tuning", "Track Realignment"]
}, {
  title: "New Door Installation",
  description: "Upgrade your curb appeal with premium, energy-efficient residential garage doors.",
  icon: House,
  subServices: ["Custom Styles", "Insulated Doors", "Modern Glass Doors", "Wood Overlay"]
}, {
  title: "Commercial Services",
  description: "Heavy-duty security gates and rolling steel doors for Kingwood & surrounding area businesses and warehouses.",
  icon: Building2,
  subServices: ["Sectional Doors", "Rolling Steel", "High-Speed Doors", "Dock Equipment"]
}, {
  title: "24/7 Emergency Support",
  description: "Same day response for urgent repairs any time of the day or night.",
  icon: Droplets,
  subServices: ["Off-Track Repair", "Security Securing", "Weekend Service", "Same Day Fix"]
}, {
  title: "Custom Projects",
  description: "Specialized hardware and aesthetic finishes tailored to your unique property needs.",
  icon: WandSparkles,
  subServices: ["Decorative Hardware", "Custom Colors", "Smart Integration", "Quiet-Glide Systems"]
}, {
  title: "Maintenance Plans",
  description: "Preventative care to extend the life of your door and ensure family safety.",
  icon: CircleCheck,
  subServices: ["Safety Inspections", "Lubrication Service", "Balance Testing", "Hardware Tightening"]
}];
export default function ServiceCategories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="22059" id="expert-garage-services" className="relative py-24 overflow-hidden !bg-[hsl(0,0%,96%)]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[60%] h-[60%] bg-white/40 rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 max-w-7xl">
        <div ref={ref} className={cn("text-center mb-16 transition-all duration-700 transform", isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10')}>
          
          <h3 className="text-4xl md:text-5xl font-bold text-foreground font-serif uppercase mb-6">Garage Door repair &amp; maintenance</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans">&nbsp;We offer solutions for springs, openers, replacements, and complete system installations for both residential and commercial properties.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => <div key={index} data-index={index} className={cn("transition-all duration-700", isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10')} style={{
          transitionDelay: `${index * 100}ms`
        }}>
              <Card className={cn("group relative h-[400px] border-border bg-card transition-all duration-500 hover:shadow-xl hover:border-primary/40 hover:-translate-y-1 rounded-xl", index === 3 ? 'hover:overflow-visible' : 'overflow-hidden')}>
                <div className="absolute inset-0 bg-transparent opacity-0 transition-all duration-500 group-hover:bg-[rgba(27,132,57,0.50)] group-hover:opacity-100 backdrop-blur-sm z-0" />
                
                <div className="relative z-10 h-full flex flex-col p-10 transition-transform duration-500 group-hover:-translate-y-4 !bg-transparent">
                  <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-primary/10 text-primary transition-all duration-500 group-hover:bg-primary group-hover:text-white group-hover:scale-110 shadow-sm">
                    <service.icon className="w-8 h-8" strokeWidth={2} />
                  </div>

                  <div className="mb-4">
                    <CardTitle className="text-2xl font-bold text-foreground mb-3 font-serif uppercase group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </div>

                  <div className="mt-auto pt-8 border-t border-primary/20 opacity-0 translate-y-8 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                    <ul className="space-y-2">
                      {service.subServices.map((sub, i) => <li key={i} className="flex items-center text-sm font-medium text-foreground/80">
                          <CircleCheck className="w-4 h-4 text-primary mr-2 shrink-0" />
                          {sub}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </Card>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <Link to="https://schaefergaragedoors.com/contact-us/">
            {}
            <Button size="lg" className="bg-primary text-white rounded-md px-4 py-2 font-medium transition-all duration-300">
              Get A Free Estimate
            </Button>
          </Link>
        </div>
      </div>
    <style dangerouslySetInnerHTML={{
      __html: `
        
        .btn-primary {
          background-color: var(--primary);
          color: white;
        }

        
        button, .btn {
          background-color: var(--primary);
          color: white;
        }
      `
    }} />
    </section>;
}